import React, { useMemo } from 'react'
import {
    Box,
    Center,
    Grid,
    Heading,
    List,
    ListIcon,
    ListItem,
    Text,
    useTheme,
} from '@chakra-ui/react'
import { EmotionIconProps } from '@emotion-icons/emotion-icon'
import { Circle } from '@emotion-icons/fa-solid'
import {
    AllExperienceLevels,
    AllImportance,
    EducationImportance,
    ExperienceLevel,
    Job,
} from 'entities/job'
import { pluralize } from 'lib/text'

const LevelAsIcon = ({ level }: { level: ExperienceLevel }) => {
    const levelAsNumber =
        level === AllExperienceLevels.Beginner
            ? 1
            : level === AllExperienceLevels.Intermediate
            ? 2
            : 3

    return (
        <Grid gridTemplateColumns={'repeat(3, 1fr)'} maxWidth={'82px'} gridGap={'2px'}>
            {[1, 2, 3].map(i => {
                return (
                    <Box
                        backgroundColor={
                            i <= levelAsNumber ? 'secondary.500' : 'secondary.300'
                        }
                        height={'3px'}
                        borderRadius={'1px'}
                        key={i}
                    />
                )
            })}
        </Grid>
    )
}

const RowIcon = (props: EmotionIconProps) => (
    <Box pb={'4px'}>
        <Circle {...props} />
    </Box>
)

const TitleWithLevel = ({ name, level }: { name: string; level: ExperienceLevel }) => {
    const { cardSizes } = useTheme()

    return (
        <Box width={cardSizes.levelCard} maxWidth={cardSizes.levelCard}>
            <Text
                variant={'bodyTextSmall'}
                fontWeight={600}
                textTransform={'capitalize'}
                noOfLines={2}
                title={name}
            >
                {name}
            </Text>
            <Text variant={'bodyTextSmall'} textTransform={'capitalize'} mb={'6px'}>
                {level}
            </Text>
            <LevelAsIcon level={level} />
        </Box>
    )
}

const ListRow = ({ children }: { children: React.ReactNode }) => {
    return (
        <ListItem display={'flex'} alignItems={'baseline'}>
            <ListIcon
                as={RowIcon}
                color="secondary.500"
                boxSize={'5px'}
                display={'block'}
                mr={'16px'}
            />
            <div>{children}</div>
        </ListItem>
    )
}

const ListRowWithText = ({ children }: { children: React.ReactNode }) => {
    return (
        <ListRow>
            <Text variant={'bodyTextSmall'} width={'100%'}>
                {children}
            </Text>
        </ListRow>
    )
}

const FormalEducationRow = ({
    formalEducation,
}: {
    formalEducation?: EducationImportance
}) => {
    if (formalEducation === AllImportance.Important) {
        return <ListRowWithText>An academic degree in the relevant field</ListRowWithText>
    } else if (formalEducation === AllImportance.NiceToHave) {
        return (
            <ListRowWithText>
                An academic degree in the relevant field is good to have
            </ListRowWithText>
        )
    }
    return null
}

export const IdealCandidate = ({
    job,
}: {
    job: Pick<
        Job,
        | 'formalEducation'
        | 'tags'
        | 'techExperienceRequirements'
        | 'spokenLanguages'
        | 'yearsOfCommercialExperience'
    >
}) => {
    const { cardSizes } = useTheme()

    const {
        spokenLanguages,
        yearsOfCommercialExperience,
        tags,
        techExperienceRequirements,
        formalEducation,
    } = job

    const hasSpokenLanguages = (spokenLanguages?.length ?? 0) > 0
    const hasTechExperience = (techExperienceRequirements?.length ?? 0) > 0

    if (
        !hasSpokenLanguages &&
        !hasTechExperience &&
        !yearsOfCommercialExperience &&
        (!formalEducation || formalEducation === 'not_important')
    ) {
        return null
    }

    const techExperienceRequirementsWithLabels = useMemo(() => {
        return techExperienceRequirements?.map(t => {
            const matchingTag = tags.find(tag => tag.id === t.id)
            return {
                ...t,
                label: matchingTag?.label,
            }
        })
    }, [techExperienceRequirements, tags])

    const gridProps = useMemo(
        () => ({
            gridTemplateColumns: `repeat(auto-fill, ${cardSizes.levelCard})`,
            gridGap: '20px',
            gridColumnGap: '32px',
            mt: '16px',
        }),
        [],
    )

    return (
        <Box
            maxWidth={{ base: '100%', md: '438px' }}
            backgroundColor={'gray.bg_light'}
            borderRadius={'16px'}
            p={{ base: '16px', md: '36px' }}
        >
            <Center mb={{ base: '16px', md: '24px' }}>
                <Heading size={'sm'} textAlign={'center'}>
                    What makes you a perfect
                    <br /> candidate for this role
                </Heading>
            </Center>
            <List spacing={'16px'}>
                <FormalEducationRow formalEducation={formalEducation} />
                {yearsOfCommercialExperience && (
                    <ListRow>
                        <Text
                            fontWeight={'600'}
                            variant={'bodyTextSmall'}
                            display={'inline'}
                            mr={'2px'}
                        >
                            {yearsOfCommercialExperience}+
                        </Text>{' '}
                        {pluralize(yearsOfCommercialExperience, 'year')} of commercial
                        experience
                    </ListRow>
                )}
                {hasTechExperience && (
                    <ListRow>
                        Corresponding level of skills:
                        <Grid {...gridProps} mb={'12px'}>
                            {techExperienceRequirementsWithLabels?.map(t => {
                                if (!t.label) return null
                                return (
                                    <TitleWithLevel
                                        name={t.label}
                                        level={t.level}
                                        key={t.label}
                                    />
                                )
                            })}
                        </Grid>
                    </ListRow>
                )}
                {hasSpokenLanguages && (
                    <ListRow>
                        Language skills:
                        <Grid {...gridProps} mb={'8px'}>
                            {spokenLanguages?.map(lang => {
                                return (
                                    <TitleWithLevel
                                        key={lang.name}
                                        name={lang.name}
                                        level={lang.level}
                                    />
                                )
                            })}
                        </Grid>
                    </ListRow>
                )}
            </List>
        </Box>
    )
}
